var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/* eslint-disable no-param-reassign */
/**
 * @module cercaliagl/service/Autocomplete
 */
import Service from './Service';
import { KEY, CountriesPortalBefore } from '../globals';
import LonLat from '../LonLat';
import JaroWinkler from '../JaroWinkler';
import { i18n } from '../dictionary';
import { countWords, convertDmsToDecimal } from '../util';
import Servers from '../servers';
import { assert } from '../AssertionError';
import VAutocomplete, { EventType } from '../view/VAutocomplete';
/**
 * @typedef {Object} AutocompleteOptions
 * @property {Function} [callbackSuggestionsFunction] Callback function when suggestions changed.
 * @property {Function} [callbackFunction] Callback function when selecting autocomplete element.
 * @property {Function|undefined} [onInputFunction] Callback function when on input event.
 * @property {string} autocompleteId `<div id="xxxx">` element ID where create add autocomplete functionality.
 * @property {Element} [element] Element
 * @property {string} [placeholder] Input placeholder. If not specified default is `Address`.
 * @property {boolean} [hnrt] Apply a tolerance in house number ranges. Cercalia suggest search prioritize address that contains the house numbers searched. This parameters lets you
 * apply a tolerance in the house numbers ranges available in the database (+/-50 numbers in general, and +/- 500 in USA), in order to avoid some gaps in this database.
 * Values: <br/>
 * `false`: don’t apply. Default value <br/>
 * `true`: apply
 * @property {string} [label] Label text for placeholder
 * @property {boolean} [closeSuggestionsIfInputIsEmpty] Close suggestions if input is empty. Default `false`.
 * @property {number} [maxCandidates] Max candidates to show. Default `5`.
 * @property {string|null} [searchInsideCountry] Autocomplete search inside a country (3 characters ISO code), for exemple: `ESP`. Default value null (all countries).
 * @property {string|null} [searchInsideRegion] Autocomplete search inside a region (Country ISO code + Region code), for exemple: `ESPMAD` or several regions, for example: `ESPCAT,ESPMAD,ESPVAL`.
 * Please, contact with Nexus if you need to know region codes.
 * @property {string|null} [searchInsideProvince] Autocomplete search inside a province (Country ISO code + Province code), for exemple: `ESP08` (Barcelona), `ESP17` (Girona) or several provinces, for example: `ESP08,ESP17`.
 * Please, contact with Nexus if you need to know province codes.
 * @property {string|null} [searchInsideMunicipality] Autocomplete search inside a municipality (Country ISO code + Province code), for exemple: `ESP410917` (Sevilla), `ESP502973` (Zaragoza) or several municipality,
 * for example: `ESP08,ESP17`.
 * Please, contact with Nexus if you need to know municipality codes.
 * @property {string|null} [searchInsideLocality] Autocomplete search inside a locality for exemple: `ESP17240205565747` (Barcelona), `ESP17240205565484` (Badalona) or several localities, for example: `ESP17240205565747,ESP17240205565484`.
 * Please, contact with Nexus if you need to know locality codes.
 * @property {boolean} [searchClosestHouseNumber] Search closest real house number in suggest. Default: `false`.
 * @property {string} [searchFilterEntity] Set filter by municipality / region / subregion / country (by entity code). You can mix that entities.
 * Examples: <br/>
 * `ESP170792,ESPCAT,ES08`<br/>SolrItem
 * `ESP,ESP08,ESP17,PRT,AND`.<br/>
 * @property {string|Array.<string>} [searchPriorityEntity] Set priority municipality / region / subregion / country (by entity code). This filter, prioritizes the results that are in the
 * specified regions above the other candidates, but does not eliminate the results from other regions or countries.
 * This filter can be combined with the "csr" filter for maximum results filter beyond the priority. Example: `ESPVAL`.
 * In case you need to prioritize candidates in the list of preferred regions, the regions may be separated
 * by parameters according to priority level: first element: maximum priority, second element: second level priority, etc <br/>
 * Examples: <br/>
 *  `['ESP170792', 'AND', 'ESP,FRA']`.<br/>
 *  `['ESPCAT', 'ESPMAD', 'PRT,AND,FRA']`.<br/>
 * @property {string|Array.<string>} [searchPreferredEntity] Set preferred municipality / region / subregion / country (by entity code). This filter, prioritizes the results that are in the
 * specified regions above the other candidates, but does not eliminate the results from other regions or countries.
 * This filter can be combined with the "csr" filter for maximum results filter beyond the preferred. Example: `ESPVAL`.
 * In case you need to prioritize candidates in the list of preferred regions, the regions may be separated
 * by parameters according to priority level: first element: maximum priority, second element: second level priority, etc <br/>
 * Examples: <br/>
 * @property {string} [poiCategories] Set POI categories. If not specified Cercalia will return default POI categories.
 * Categories. For example: `['C012', 'D00CAM', 'C027', 'C016']`
 *  `['ESP170792', 'AND', 'ESP,FRA']`.<br/>
 *  `['ESPCAT', 'ESPMAD', 'PRT,AND,FRA']`.<br/>
 * @property {boolean} [floatingLabel] Floating label for input UI element. Default `false`.
 * @property {string|Array.<string>} [lang] Set preferred lang to return suggestions. Default `null`.
 */
/**
 * @typedef {Object} GeEntitySuggest
 * @property {string|null} [article]
 * @property {import("../servers.js").ObjectJSON|null} [city]
 * @property {import('../servers').Coord|null} [coord]
 * @property {import('../servers').ObjectJSON|null} [country]
 * @property {string|null} [dist]
 * @property {string|null} [frc]
 * @property {import("../servers.js").ObjectJSON|null} [housenumber]
 * @property {string|null} [id]
 * @property {import("../servers.js").ObjectJSON|null} [intersection]
 * @property {import("../servers.js").ObjectJSON|null} [km]
 * @property {string|null} [kmh]
 * @property {string|null} [label]
 * @property {import("../servers.js").GeEntityGeometry|null} [geometry]
 * @property {import('../servers').ObjectJSON|null} [municipality]
 * @property {import('../servers').ObjectJSON} [name]
 * @property {string|null} [pos]
 * @property {import('../servers').ObjectJSON|import('../servers').PostalCode|null} [postalcode]
 * @property {string|null} [prefix]
 * @property {ObjectJSON|null} [region]
 * @property {string|null} [sname]
 * @property {ObjectJSON|string|null} [subcategory_id]
 * @property {ObjectJSON|null} [subregion]
 * @property {string|null} [type]
 * @property {string|null} [zonename]
 * @property {Array.<import('../servers').ParamEntity>} [param]
 * @property {string} [id]
 * @property {string} [category_id]
 * @property {string} [formatted_address]
 * @property {Object} [poi_data]
 */
/**
 * @enum {string}
 */
export var FilterTypes = {
    CITIES: 'ct',
    STREETS: 'st',
    POIS: 'p',
    POSTAL_CODES: 'pc',
    POSTAL_CODES_SHORT: 'pc2',
};
/**
 * @classdesc
 *
 * <b>Javascript ES5</b>
 * ```js
 * const autocopmplete = new cercaliagl.service.Autocomplete({ ... });
 * ```
 *
 * This class add suggest autocomplete funcionality to an input for search addresses.<br/>
 * The input must be an empty `div` HTML element and it can be passed by id (`autocompleteId` option) or by DOM Element reference.
 * <br/>
 * In the creation of this service, the `div` element will contains more DOM elements inside.
 * <br/>
 * <br/>
 * This service allow to apply filters to search inside countries, regions, subregions, municipalities, localities, etc.
 * By default it searches for addresses, localities, postal codes and POIs. But this behaviour can be modified.<br/>
 * <br/>
 * You can see all constructor options to modify this functionality.
 * Finally it is mandatory to assign a `callbackFunction` which will be called when an address is selected.
 * <br/>
 * <br/>
 * Example usage:
 *
 * ```js
 * const autocompleteService = new cercalia.service.Autocomplete({
 *   element: document.getElementById('autocomplete'),
 *   label: 'Search address example',
 *   floatingLabel: true,
 *   callbackFunction: (data) => {
 *     document.querySelector('#response').innerHTML = JSON.stringify(data, null, 2);
 *   },
 * });
 * ```
 *
 * @api
 */
var Autocomplete = /** @class */ (function (_super) {
    __extends(Autocomplete, _super);
    /**
     * @param {AutocompleteOptions} options Options
     */
    function Autocomplete(options) {
        var _a;
        var _this = _super.call(this) || this;
        /**
         * @const
         * @type {string}
         */
        _this.DEFAULT_FILTER_TYPE_ = [FilterTypes.CITIES, FilterTypes.STREETS, FilterTypes.POIS, FilterTypes.POSTAL_CODES, FilterTypes.POSTAL_CODES_SHORT].join(',');
        /**
         * @private
         * @type {string}
         */
        _this.autocompleteId_ = options.element ? options.element.getAttribute('id') : options.autocompleteId;
        /**
         * @private
         * @type {Element}
         */
        _this.element_ = options.element ? options.element : document.getElementById(_this.autocompleteId_);
        /**
         * @private
         * @type {string}
         */
        _this.searchFilterType_ = _this.DEFAULT_FILTER_TYPE_;
        /**
         * @private
         * @type {string|undefined}
         */
        _this.placeholder_ = options.placeholder ? options.placeholder : undefined;
        /**
         * @private
         * @type {string|null|undefined}
         */
        _this.searchInsideCountry_ = options.searchInsideCountry ? options.searchInsideCountry : undefined;
        /**
         * @private
         * @type {string|null|undefined}
         */
        _this.searchInsideRegion_ = options.searchInsideRegion ? options.searchInsideRegion : undefined;
        /**
         * @private
         * @type {string|null|undefined}
         */
        _this.searchInsideProvince_ = options.searchInsideProvince ? options.searchInsideProvince : undefined;
        /**
         * @private
         * @type {boolean}
         */
        _this.closeSuggestionsIfInputIsEmpty_ = (_a = options.closeSuggestionsIfInputIsEmpty) !== null && _a !== void 0 ? _a : false;
        /**
         * @private
         * @type {string|null|undefined}
         */
        _this.searchInsideMunicipality_ = options.searchInsideMunicipality ? options.searchInsideMunicipality : undefined;
        /**
         * @private
         * @type {string|null|undefined}
         */
        _this.searchInsideLocality_ = options.searchInsideLocality ? options.searchInsideLocality : undefined;
        /**
         * @private
         * @type {boolean}
         */
        _this.searchClosestHouseNumber_ = options.searchClosestHouseNumber ? options.searchClosestHouseNumber : false;
        /**
         * @private
         * @type {string|undefined}
         */
        _this.searchFilterEntity_ = options.searchFilterEntity ? options.searchFilterEntity : undefined;
        /**
         * @private
         * @type {string|Array.<string>|undefined}
         */
        _this.lang_ = options.lang ? options.lang : undefined;
        if (Array.isArray(_this.lang_)) {
            _this.lang_ = _this.lang_.join(',');
        }
        /**
         * @private
         * @type {string|Array.<string>|undefined}
         */
        _this.searchPriorityEntity_ = options.searchPriorityEntity ? options.searchPriorityEntity : undefined;
        /**
         * @private
         * @type {string|Array.<string>}
         */
        _this.searchPreferredEntity_ = options.searchPreferredEntity ? options.searchPreferredEntity : undefined;
        /**
       * @private
       * @type {cercalia.LonLat|undefined}
       */
        _this.searchCoordinateReference_ = options.searchCoordinateReference ? options.searchCoordinateReference : undefined;
        /**
       * @private
       * @type {number|null}
       */
        _this.searchInsideRadius_ = options.searchInsideRadius ? options.searchInsideRadius : null;
        /**
         * @private
         * @type {string|undefined}
         */
        _this.poiCategories_ = options.poiCategories ? options.poiCategories : undefined;
        /**
         * @private
         * @type {number|null}
         */
        _this.maxCandidates_ = options.maxCandidates ? options.maxCandidates : 5;
        /**
         * @private
         * @type {boolean|undefined}
         */
        _this.hnrt_ = options.hnrt ? options.hnrt : false;
        /**
         * @private
         * @type {Function|null|undefined}
         */
        _this.callbackFn_ = options.callbackFunction ? options.callbackFunction : null;
        /**
         * @private
         * @type {Function|null|undefined}
         */
        _this.callbackSuggestionsFunction_ = options.callbackSuggestionsFunction ? options.callbackSuggestionsFunction : null;
        /**
         * @private
         * @type {Function|undefined}
         */
        _this.onInputFunction_ = options.onInputFunction ? options.onInputFunction : undefined;
        /**
         * @private
         * @type {GeEntitySuggest|null|undefined}
         */
        _this.response_ = null;
        /**
         * @private
         * @type {XMLHttpRequest|null}
         */
        _this.suggestXhr_ = null;
        /**
         * @private
         * @type {XMLHttpRequest|null}
         */
        _this.suggestPoisXhr_ = null;
        /**
         * @private
         * @type {Array.<import('../servers').SolrItem>}
         */
        _this.elementsSuggest_ = [];
        /**
         * @private
         * @type {Array.<import('../servers').SolrItem>}
         */
        _this.elementsSuggestPois_ = [];
        /**
         * @private
         * @type {JaroWinkler}
         */
        _this.jaroWinkler_ = new JaroWinkler();
        /**
         * @private
         * @type {VAutocomplete}
         */
        _this.vAutocomplete_ = new VAutocomplete(_this.autocompleteId_, options.label, options.floatingLabel);
        _this.initialize_();
        return _this;
    }
    /**
     * Assign a callback function after selecting a candidate from the suggest list
     * @param {Function} callbackFn Callback Function.
     * @api
     */
    Autocomplete.prototype.setCallbackFunction = function (callbackFn) {
        assert(typeof callbackFn === 'function', i18n('assertParameterIsNotFunction'));
        this.callbackFn_ = callbackFn;
    };
    /**
     * Assign a callback function after suggestions changed
     * @param {Function} callbackFn Callback Function.
     * @api
     */
    Autocomplete.prototype.setCallbackSuggestionsFunction_ = function (callbackSuggestionsFunction) {
        assert(typeof callbackSuggestionsFunction === 'function', i18n('assertParameterIsNotFunction'));
        this.callbackSuggestionsFunction_ = callbackSuggestionsFunction;
    };
    /**
     * Get Geocoding JSON server response. It returns `null` if address is not selected.
     * @return {GeEntitySuggest|null|undefined} JSON server response.
     * @api
     */
    Autocomplete.prototype.getGeocoding = function () {
        return this.response_;
    };
    /**
     * Search inside a country. For example: `'ESP,FRA,GBR'` or `['ESP','FRA','GBR']`
     * @param {string|Array.<string>|null} countriesId Country or array of countries id.
     * @api
     */
    Autocomplete.prototype.setSearchInsideCountry = function (countriesId) {
        if (Array.isArray(countriesId)) {
            countriesId = countriesId.join(',');
        }
        this.searchInsideCountry_ = countriesId ? countriesId.replace(/ +(?= )/g, '') : undefined;
    };
    /**
     * Search inside a region. For example: `ESPMAD` (Comunidad de Madrid), `ESPCAT` (Catalunya), etc..
     * @param {string|Array.<string>|null} regionsId Region or array of regions id.
     * @api
     */
    Autocomplete.prototype.setSearchInsideRegion = function (regionsId) {
        if (Array.isArray(regionsId)) {
            regionsId = regionsId.join(',');
        }
        this.searchInsideRegion_ = regionsId !== null ? regionsId.replace(/ +(?= )/g, '') : undefined;
    };
    /**
     * Search inside a region. For example: `ESP17` (Girona), `ESP08` (Barcelona) etc..
     * @param {string|Array.<string>|null} provinceId Region or array of regions id.
     * @api
     */
    Autocomplete.prototype.setSearchInsideProvince = function (provinceId) {
        if (Array.isArray(provinceId)) {
            provinceId = provinceId.join(',');
        }
        this.searchInsideProvince_ = provinceId !== null ? provinceId.replace(/ +(?= )/g, '') : undefined;
    };
    /**
     * Search inside a municipality. For example: `ESP280796` (Madrid), `ESP080193` (Barcelona), etc..
     * @param {string|Array.<string>|null} municipalityId Municipality id or array of municipality id.
     * @api
     */
    Autocomplete.prototype.setSearchInsideMunicipality = function (municipalityId) {
        if (Array.isArray(municipalityId)) {
            municipalityId = municipalityId.join(',');
        }
        this.searchInsideMunicipality_ = municipalityId !== null ? municipalityId.replace(/ +(?= )/g, '') : undefined;
    };
    /**
     * Search inside a locality. For example: `ESP17240205544993` (Vallecas), `ESP17240205545872` (La Barceloneta), etc..
     * @param {string|Array.<string>|null} localityId Locality or array of localities id.
     * @api
     */
    Autocomplete.prototype.setSearchInsideLocality = function (localityId) {
        if (Array.isArray(localityId)) {
            localityId = localityId.join(',');
        }
        this.searchInsideLocality_ = localityId !== null ? localityId.replace(/ +(?= )/g, '') : undefined;
    };
    /**
     * Set filter by municipality / region / subregion (by entity code). You can mix that entities.
     * Parameter examples: <br/>
     * `ESP170792,ESPCAT,ES08` or * `[ESP170792, ESPCAT, ES08`]<br/>
     * `ESP,ESP08,ESP17,PRT,AND`.<br/>
     * @param {string|Array.<string>} entityId Entity id.
     */
    Autocomplete.prototype.setSearchFilterEntity = function (entityId) {
        if (entityId instanceof Array) {
            entityId = entityId.join(',');
        }
        this.searchFilterEntity_ = entityId !== null ? entityId.replace(/ +(?= )/g, '') : undefined;
    };
    /**
     * Set preferred municipality / region / subregion (by entity code). This filter, prioritizes the results that are in the
     * specified regions above the other candidates, but does not eliminate the results from other regions or countries.
     * This filter can be combined with the "csr" filter for maximum results filter beyond the preferred. Example: `ESPVAL`.
     * In case you need to prioritize candidates in the list of preferred regions, the regions may be separated
     * by parameters according to priority level: first element: maximum priority, second element: second level priority, etc <br/>
     * Parameter examples: <br/>
     *  `['ESP170792', 'AND', 'ESP,FRA']`.<br/>
     *  `['ESPCAT', 'ESPMAD', 'PRT,AND,FRA']`.<br/>
     * @param {string|Array.<string>|undefined} entities Entities.
     */
    Autocomplete.prototype.setSearchPriorityEntity = function (entities) {
        this.searchPriorityEntity_ = entities;
    };
    /**
     * Set preferred lang to return suggestions
     * @param {string|Array.<string>|undefined} lang setLang.
     */
    Autocomplete.prototype.setLang = function (lang) {
        this.lang_ = Array.isArray(lang) ? lang.join(',') : lang;
    };
    /**
     * Set preferred municipality / region / subregion / country (by entity code). Not compatible with "searchPriorityEntity". This filter,
     * use a soft prioritizaton of the results that are in the specified regions above the other candidates, but does not eliminate the results
     * from other regions or countries. This filter can be combined with the `rsc` filter for maximum results filter beyond the preferred.
     * Example: `ESPVAL`. In case you need to prioritize candidates in the list of preferred regions, the regions may be separated by parameters
     * according to priority level: first element: maximum priority, second element: second level priority, etc.
     *  `['ESP170792', 'AND', 'ESP,FRA']`.<br/>
     *  `['ESPCAT', 'ESPMAD', 'PRT,AND,FRA']`.<br/>
     * @param {string|Array.<string>|null} entities Entities.
     */
    Autocomplete.prototype.setSearchPreferredEntity = function (entities) {
        this.searchPreferredEntity_ = entities;
    };
    /**
   * Filter results by proximity to a point (coordinate). It is a restrictive filter (only returns candidates within the radius), prioritizing results based on proximity to the search point.
   * `searchInsideRadius` option must be provided. Use `setSearchCoordinateReference` method.
   * @param {cercalia.LonLat|null} lonLat Coordinate reference. `null` value deactive behaviour.
   * @api
   */
    Autocomplete.prototype.setSearchCoordinateReference = function (lonLat) {
        this.searchCoordinateReference_ = lonLat;
    };
    /**
     * Set radius for geographic search. Complementary option for option `searchCoordinateReference`.
     * @param {number|null} radius Radius
     * @api
   */
    Autocomplete.prototype.setSearchInsideRadius = function (radius) {
        this.searchInsideRadius_ = radius;
    };
    /**
     * Set POI categories. If not specified Cercalia will return default POI categories.
     * @param {string|Array.<string>|null} categories Categories. For example: `['C012', 'D00CAM', 'C027', 'C016']`
     * @api
     */
    Autocomplete.prototype.setPoiCategories = function (categories) {
        if (Array.isArray(categories)) {
            categories = categories.join(',');
        }
        this.poiCategories_ = categories !== null ? categories.replace(/ +(?= )/g, '') : undefined;
    };
    /**
     * Search within type specified. Possible values are: `st` (streets), `ct` (locality), `p` (pois), `all|null`. It is possible specify more than one value. Example : `st,ct`
     * @param {string|Array.<string>|null} type Type or array of types. For example: `['ct','pois','st']`.
     * @api
     */
    Autocomplete.prototype.setSearchFilterType = function (type) {
        if (Array.isArray(type)) {
            type = type.join(',');
        }
        this.searchFilterType_ = type !== null ? type.replace(/ +(?= )/g, '') : this.DEFAULT_FILTER_TYPE_;
    };
    /**
     * Initializes the element
     * @private
     */
    Autocomplete.prototype.initialize_ = function () {
        var _this = this;
        this.vAutocomplete_.on(EventType.AUTOCOMPLETE_CHANGE, function (text) {
            if (Autocomplete.checkAndNormalizaCoordinates(text) === null) {
                _this.onAutocompleteChange_(text);
            }
        });
        this.vAutocomplete_.on(EventType.AUTOCOMPLETE_CHANGE_WITHOUT_MIN_CHARACTERS, function (text) {
            if (!text && _this.closeSuggestionsIfInputIsEmpty_) {
                _this.vAutocomplete_.hideSuggestions();
            }
            if (_this.onInputFunction_) {
                _this.onInputFunction_(text);
            }
        });
        this.vAutocomplete_.on(EventType.AUTOCOMPLETE_SELECTED, function (element) { return _this.geocodeAddress_(element); });
        this.vAutocomplete_.on(EventType.AUTOCOMPLETE_ENTER, function (text) {
            var coordinates = Autocomplete.checkAndNormalizaCoordinates(text);
            if (coordinates !== null) {
                _this.searchCoordinates_(coordinates);
            }
        });
    };
    /**
     * @private
     * @param {string} coordinates Coordinates
     */
    Autocomplete.prototype.searchCoordinates_ = function (coordinates) {
        var arrCoord = coordinates.split(',');
        var lon = parseFloat(arrCoord[1]);
        var lat = parseFloat(arrCoord[0]);
        var position = new LonLat(lon, lat);
        if (this.callbackFn_) {
            this.callbackFn_.call(this, position);
        }
        else {
            console.error('No callbackfunction defined', this.response_); // eslint-disable-line no-console
        }
    };
    /**
     * Close autocomplete div.
     * @api
     */
    Autocomplete.prototype.close = function () {
        this.vAutocomplete_.hideSuggestions();
    };
    /**
     * @private
     * @param {string} str String
     * @return {string|null} If string contains coordinates then normalize it, else return null
     */
    Autocomplete.checkAndNormalizaCoordinates = function (str) {
        var search = str.trim();
        var patternCoordinates = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;
        // eslint-disable-next-line max-len
        var patternCoordinatesDMS = /^([0-9]{1,2})[:|°|º][\s+]?([0-9]{1,2})[:|'|′]?[\s+]?([0-9]{1,2}(?:\.[0-9]+){0,1})?["|″][\s+]?([N|S])[,|\s+][\s+]?([0-9]{1,3})[:|°|º][\s+]?([0-9]{1,2})[:|'|′]?[\s+]?([0-9]{1,2}(?:\.[0-9]+){0,1})?["|″][\s+]?([E|W])$/;
        if (patternCoordinates.test(search)) {
            return search;
        }
        if (patternCoordinatesDMS.test(search)) {
            return convertDmsToDecimal(search);
        }
        return null;
    };
    /**
     * @private
     * @param {string} search Search Text.
     */
    Autocomplete.prototype.onAutocompleteChange_ = function (search) {
        if (this.suggestXhr_) {
            this.suggestXhr_.abort();
            this.suggestXhr_ = null;
            this.elementsSuggest_ = [];
        }
        if (this.suggestPoisXhr_) {
            this.suggestPoisXhr_.abort();
            this.suggestPoisXhr_ = null;
            this.elementsSuggestPois_ = [];
        }
        var filters = this.searchFilterType_ !== '' ? this.searchFilterType_.split(',') : [];
        if (filters.length > 0) {
            this.requestSuggest_(search);
            // Only when there are more than one word.
            if ((countWords(search) > 1 && filters.indexOf('p') !== -1) || (filters.length === 1 && filters[0] === 'p')) {
                this.requestSuggestPois_(search);
            }
        }
    };
    /**
     * @private
     * @param {string} search Search string.
     */
    Autocomplete.prototype.requestSuggest_ = function (search) {
        var url = this.prepareRequestSuggestUrl_(search);
        if (url !== null) {
            this.suggestXhr_ = new XMLHttpRequest();
            this.suggestXhr_.open('GET', encodeURI(url));
            this.suggestXhr_.onload = this.handleRequestSuggest_.bind(this, search);
            this.suggestXhr_.send();
        }
    };
    /**
     * @private
     * @param {string} search Search Text.
     */
    Autocomplete.prototype.requestSuggestPois_ = function (search) {
        var url = this.prepareRequestSuggestPoisUrl_(search);
        this.suggestPoisXhr_ = new XMLHttpRequest();
        this.suggestPoisXhr_.open('GET', encodeURI(url));
        this.suggestPoisXhr_.onload = this.handleRequestSuggestPois_.bind(this, search);
        this.suggestPoisXhr_.send();
    };
    /**
     * @private
     * @param {string} search Search.
     * @return {string|null} Url with parameters. If null no request.
     */
    Autocomplete.prototype.prepareRequestSuggestUrl_ = function (search) {
        var searchType = this.searchFilterType_.replace(/[,]?p/, ''); // Si conté 'p' no el passem.
        if (searchType.length > 0) {
            var url = "".concat(Servers.solr, "?");
            url += "key=".concat(KEY, "&");
            url += "getype=".concat(this.searchFilterType_, "&");
            url += "t=".concat(search);
            url += (this.searchCoordinateReference_ ? "&pt=".concat(this.searchCoordinateReference_.getLat(), ",").concat(this.searchCoordinateReference_.getLon()) : '');
            url += (this.searchInsideRadius_ ? "&d=".concat(this.searchInsideRadius_) : '');
            url += (this.searchInsideCountry_ ? "&ctryc=".concat(this.searchInsideCountry_) : '');
            url += (this.searchInsideRegion_ ? "&regc=".concat(this.searchInsideRegion_) : '');
            url += (this.searchInsideProvince_ ? "&subregc=".concat(this.searchInsideProvince_) : '');
            url += (this.searchInsideMunicipality_ ? "&munc=".concat(this.searchInsideMunicipality_) : '');
            url += (this.searchInsideLocality_ ? "&ctc=".concat(this.searchInsideLocality_) : '');
            url += (this.hnrt_ ? '&hnrt=1' : '');
            url += (this.searchFilterEntity_ ? "&rsc=".concat(this.searchFilterEntity_) : '');
            url += (this.lang_ ? "&lang=".concat(this.lang_) : '');
            if (this.searchPriorityEntity_) {
                if (!Array.isArray(this.searchPriorityEntity_)) {
                    url += "&rscp=".concat(this.searchPriorityEntity_);
                }
                else {
                    for (var i = 0; i < this.searchPriorityEntity_.length; i += 1) {
                        url += "&rscp".concat(i + 1, "=").concat(this.searchPriorityEntity_[i]);
                    }
                }
            }
            if (this.searchPreferredEntity_) {
                if (!Array.isArray(this.searchPreferredEntity_)) {
                    url += "&rsclp=".concat(this.searchPreferredEntity_);
                }
                else {
                    for (var j = 0; j < this.searchPreferredEntity_.length; j += 1) {
                        url += "&rsclp".concat(j + 1, "=").concat(this.searchPreferredEntity_[j]);
                    }
                }
            }
            return url;
        }
        return null;
    };
    /**
     * @private
     * @param {string} search Search
     * @return {string} Url with parameters.
     */
    Autocomplete.prototype.prepareRequestSuggestPoisUrl_ = function (search) {
        var url = "".concat(Servers.solr, "?")
            + "key=".concat(KEY, "&t=").concat(search, "&pois=1").concat(this.searchCoordinateReference_ ? "&pt=".concat(this.searchCoordinateReference_.getLat(), ",").concat(this.searchCoordinateReference_.getLon()) : '').concat(this.searchInsideRadius_ ? "&d=".concat(this.searchInsideRadius_) : '').concat(this.searchInsideCountry_ ? "&ctryc=".concat(this.searchInsideCountry_) : '').concat(this.searchInsideRegion_ ? "&regc=".concat(this.searchInsideRegion_) : '').concat(this.searchInsideProvince_ ? "&subregc=".concat(this.searchInsideProvince_) : '').concat(this.searchInsideMunicipality_ ? "&munc=".concat(this.searchInsideMunicipality_) : '').concat(this.searchInsideLocality_ ? "&ctc=".concat(this.searchInsideLocality_) : '').concat(this.searchFilterEntity_ ? "&rsc=".concat(this.searchFilterEntity_) : '').concat(this.lang_ ? "&lang=".concat(this.lang_) : '').concat(this.poiCategories_ ? "&poicat=".concat(this.poiCategories_) : '');
        if (this.searchPriorityEntity_) {
            if (!Array.isArray(this.searchPriorityEntity_)) {
                url += "&rscp=".concat(this.searchPriorityEntity_);
            }
            else {
                for (var i = 0; i < this.searchPriorityEntity_.length; i += 1) {
                    url += "&rscp".concat(i + 1, "=").concat(this.searchPriorityEntity_[i]);
                }
            }
        }
        if (this.searchPreferredEntity_) {
            if (!Array.isArray(this.searchPreferredEntity_)) {
                url += "&rsclp=".concat(this.searchPreferredEntity_);
            }
            else {
                for (var j = 0; j < this.searchPreferredEntity_.length; j += 1) {
                    url += "&rsclp".concat(j + 1, "=").concat(this.searchPreferredEntity_[j]);
                }
            }
        }
        return url;
    };
    /**
     * @private
     * @param {string} search Search term.
     */
    Autocomplete.prototype.handleRequestSuggest_ = function (search) {
        var _this = this;
        if (this.suggestXhr_.status === 200) {
            var data = JSON.parse(/** @type {string} */ (this.suggestXhr_.response));
            this.elementsSuggest_ = data.response.docs.map(function (item) {
                var label;
                var localidadNorm;
                var provinciaNorm;
                // 20170214 => Eliminar el municipi, si hi ha localitat. Si només retorna municipi, ho mantenim
                if (item.localidad_nombre) {
                    if (['CAN', 'USA'].indexOf(item.pais_id) !== -1) {
                        localidadNorm = item.localidad_nombre;
                    }
                    else if (item.localidad_nombre === item.municipio_nombre) {
                        localidadNorm = item.localidad_nombre;
                    }
                    else {
                        localidadNorm = "".concat(item.localidad_nombre, ", ").concat(item.municipio_nombre);
                    }
                }
                else {
                    localidadNorm = item.municipio_nombre;
                }
                // 20170214 => Eliminar el municipi, si hi ha localitat. Si només retorna municipi, ho mantenim
                if (['ESP', 'GBR', 'NZL'].indexOf(item.pais_id) !== -1) {
                    provinciaNorm = item.provincia_nombre;
                }
                else if (item.comunidad_nombre && item.provincia_nombre) {
                    provinciaNorm = item.comunidad_nombre;
                }
                else if (item.comunidad_nombre && !item.provincia_nombre) {
                    provinciaNorm = item.comunidad_nombre;
                }
                else {
                    provinciaNorm = item.provincia_nombre;
                }
                // If there is postal code...
                if (item.codigo_postal) {
                    localidadNorm = "".concat(item.codigo_postal, " ").concat(localidadNorm);
                }
                if (item.pc2) {
                    label = "<b>".concat(item.pc2, "</b>, ").concat(item.pais_nombre);
                }
                else if (item.calle_id === undefined) {
                    // Localidad
                    label = "<b>".concat(localidadNorm, "</b>, ").concat(provinciaNorm ? "".concat(provinciaNorm, ", ") : '').concat(item.pais_nombre);
                    item.className = 'city';
                }
                else {
                    // Carrer
                    var portal = _this.searchClosestHouseNumber_ ? item.portal_disponible : item.portal;
                    if (CountriesPortalBefore.indexOf(item.pais_id) !== -1) {
                        label = "".concat((portal ? "".concat(portal, " ") : '') + item.calle_descripcion, ", ").concat(localidadNorm, ", ").concat(provinciaNorm ? "".concat(provinciaNorm) : '');
                    }
                    else {
                        label = "<b>".concat(item.calle_descripcion + (portal ? " ".concat(portal) : ''), "</b>, ").concat(localidadNorm, ", ").concat(provinciaNorm ? "".concat(provinciaNorm) : '');
                    }
                    item.className = 'address';
                }
                item.text = label;
                item.value = label;
                item.jarowinkler_dist = _this.jaroWinkler_.distance(search, label, false);
                return item;
            });
            if (this.callbackSuggestionsFunction_) {
                this.callbackSuggestionsFunction_(this.elementsSuggest_.concat(this.elementsSuggestPois_));
            }
            this.mergeSuggestResponses_();
        }
    };
    /**
     * @private
     * @param {string} search Search.
     */
    Autocomplete.prototype.handleRequestSuggestPois_ = function (search) {
        var _this = this;
        if (this.suggestPoisXhr_.status === 200) {
            var data = JSON.parse(/** @type {string} */ (this.suggestPoisXhr_.response));
            this.elementsSuggestPois_ = data.response.docs.map(function (item) {
                var label = "".concat(item.poi_nombre, ", ").concat(item.calle_descripcion ? "".concat(item.calle_descripcion + (item.portal ? " ".concat(item.portal) : ''), ", ") : '').concat(item.localidad_nombre);
                item.text = label;
                item.value = label;
                item.className = item.categoria_id.toUpperCase();
                item.jarowinkler_dist = _this.jaroWinkler_.distance(search, label, false);
                return item;
            });
            this.mergeSuggestResponses_();
        }
    };
    /**
     * @private
     */
    Autocomplete.prototype.mergeSuggestResponses_ = function () {
        var _a;
        var elements = this.elementsSuggest_.concat(this.elementsSuggestPois_);
        var filters = this.searchFilterType_ !== '' ? this.searchFilterType_.split(',') : [];
        if (elements.length > 0) {
            if (filters.indexOf('p') !== -1 && (this.searchInsideRadius_ === null || this.searchInsideRadius_ === undefined) && ((_a = this.elementsSuggestPois_) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                elements.sort(Autocomplete.sortSuggestElements_);
            }
            if (this.maxCandidates_) {
                elements = elements.splice(0, this.maxCandidates_);
            }
            this.vAutocomplete_.suggestions(elements);
            this.vAutocomplete_.showSuggestions();
        }
        else {
            this.vAutocomplete_.hideSuggestions();
        }
    };
    /**
     * @private
     * @param {import('../servers').SolrItem} a Solr item
     * @param {import('../servers').SolrItem} b Solr item
     * @return {number} Sort value.
     */
    Autocomplete.sortSuggestElements_ = function (a, b) {
        if (a.className !== b.className) {
            if (a.className === 'city') {
                return -1;
            }
            if (b.className === 'city') {
                return 1;
            }
            return b.jarowinkler_dist - a.jarowinkler_dist;
        }
        return b.jarowinkler_dist - a.jarowinkler_dist;
    };
    /**
     *  @private
     *  @param {import('../servers').SolrItem} item JSON Object. Data from selected element. Solr format.
     */
    Autocomplete.prototype.geocodeAddress_ = function (item) {
        var isPoi = item.categoria_id !== undefined;
        var params = "key=".concat(KEY, "&")
            + 'cmd=autocompletegeocoding&'
            + "is_poi=".concat(isPoi, "&")
            + "municipality_id=".concat(item.municipio_id, "&")
            + "country_id=".concat(item.pais_id, "&")
            + "".concat((item.id.substring(0, 2) !== 'PC' ? "city_id=".concat(item.localidad_id, "&") : '')) // avoid send localitat_id as parameter if item is a postalcode (ID starts with 'CP')
            + "".concat(item.codigo_postal ? "postalcode=".concat(item.codigo_postal, "&") : '').concat(item.calle_id ? "street_id=".concat(item.calle_id, "&") : '').concat(item.portal ? "street_num=".concat(item.portal) : '');
        var url = "".concat(Servers.servlet, "?").concat(params);
        var xhr = new XMLHttpRequest();
        xhr.open('GET', encodeURI(url));
        xhr.onload = this.handleGeocodeAddress_.bind(this, xhr, item, isPoi);
        xhr.send();
    };
    /**
     * @private
     * @param {XMLHttpRequest} xhr XMLHttpRequest
     * @param {import('../servers').SolrItem} item Item.
     * @param {boolean} isPoi `true` if item is a POI.
     */
    Autocomplete.prototype.handleGeocodeAddress_ = function (xhr, item, isPoi) {
        if (xhr.status === 200) {
            var candidates = void 0;
            var coord = void 0;
            var data = /** @type {import('../servers').GeocodingResult} */ (JSON.parse(/** @type {string} */ (xhr.response)));
            if (!isPoi) {
                if (data.cercalia.candidates.candidate.length > 1 && item.portal) {
                    this.response_ = Autocomplete.searchClosestPortal(data.cercalia.candidates.candidate, item.portal);
                }
                else {
                    this.response_ = data.cercalia.candidates.candidate[0].ge;
                    if (item.pc2) {
                        this.response_.pc2 = item.pc2;
                    }
                }
                if (!item.intersection) {
                    // normal street (no intersection)
                    candidates = data.cercalia.candidates;
                    if (candidates.urlcandidates && candidates.urlcandidates.param) {
                        this.response_.param = candidates.urlcandidates.param;
                        if (item.portal
                            && candidates.urlcommon
                            && candidates.urlcommon.param
                            && candidates.urlcommon.param.name
                            && candidates.urlcommon.param.name === 'stnum') {
                            this.response_.param[this.response_.param.length] = candidates.urlcommon.param;
                        }
                    }
                }
                else if ((!item.intersection.portal && !item.portal) || (item.intersection.portal && !item.portal)) { // has intersection
                    // si se cumple evitamos utilizar los valors de la geocodificacion
                    coord = item.intersection.coord.split(',');
                    this.response_.coord.y = coord[0], this.response_.coord.x = coord[1];
                }
                this.response_.formatted_address = item.text;
            }
            else {
                coord = item.coord.split(',');
                candidates = data.cercalia.candidates;
                this.response_ = candidates.candidate[0].ge;
                this.response_.type = 'p';
                this.response_.id = item.id;
                this.response_.category_id = item.categoria_id;
                this.response_.coord.y = coord[0], this.response_.coord.x = coord[1];
                this.response_.municipality = {
                    id: this.response_.id,
                    value: this.response_.name.value,
                };
                this.response_.postalcode = /** @type {import('../servers').PostalCode} */ ({
                    id: item.codigo_postal,
                    country_id: item.pais_id,
                    pc2: item.pc2,
                });
                this.response_.country = {
                    id: item.pais_id,
                    value: item.pais_nombre,
                };
                this.response_.name.value = item.poi_nombre;
                this.response_.formatted_address = "".concat(item.poi_nombre, ", ").concat(item.codigo_postal ? "".concat(item.codigo_postal, " ") : '').concat(item.municipio_nombre, ", ").concat(item.pais_nombre);
                this.response_.poi_data = {
                    tel: item.tel,
                    mail: item.mail,
                    web: item.web,
                };
            }
            // this.element_.setOption(this.response_, this.response_.formatted_address);
            this.vAutocomplete_.setOption(this.response_.formatted_address);
            if (this.callbackFn_) {
                this.callbackFn_.call(this, this.response_);
            }
            else {
                console.error('No callbackfunction defined', this.response_); // eslint-disable-line no-console
            }
        }
    };
    /**
     * @private
     * @param {Array.<import('../servers').GeocodingResultDataCandidate>} geList GeEntity array.
     * @param {number|string} portal Portal.
     * @return {GeEntitySuggest} GeEntity
     */
    Autocomplete.searchClosestPortal = function (geList, portal) {
        var minHouseNumberDistance = 999999;
        var geMinDistance = null;
        for (var i = 0; i < geList.length; i += 1) {
            var ge = geList[i].ge;
            if (ge.housenumber) {
                if (Number.isNaN(portal) && portal === ge.housenumber.value) {
                    return ge;
                }
                var houseNumberDistanceAct = Math.abs(portal - ge.housenumber.value);
                if (houseNumberDistanceAct < minHouseNumberDistance) {
                    geMinDistance = ge;
                    minHouseNumberDistance = houseNumberDistanceAct;
                }
            }
        }
        if (!geMinDistance) {
            geMinDistance = geList[0].ge;
        }
        return geMinDistance;
    };
    /**
     * Get autocomplete DOM element reference. This should be `<div />` element.
     * @return {Element} Autocomplete element.
     * @api
     */
    Autocomplete.prototype.getElement = function () {
        return this.element_;
    };
    /**
     * Change value of autocomplete and optionally open suggestions.
     * @param {string} text Text
     * @param {boolean} [openSuggestions] Open suggest after change input value. Default `false`
     * @api
     */
    Autocomplete.prototype.setValue = function (text, openSuggestions) {
        if (openSuggestions === void 0) { openSuggestions = false; }
        this.vAutocomplete_.setOption(text);
        if (openSuggestions) {
            var coordinates = Autocomplete.checkAndNormalizaCoordinates(text);
            if (coordinates === null) {
                this.vAutocomplete_.emit(EventType.AUTOCOMPLETE_CHANGE, text);
            }
            else {
                this.searchCoordinates_(coordinates);
            }
        }
    };
    /**
     * Set input function
     * @param {Function} onInputFunction Function
     * @api
     */
    Autocomplete.prototype.setOnInputFunction = function (onInputFunction) {
        this.onInputFunction_ = function (text) { return onInputFunction(text); };
    };
    /**
     * Set close suggestions if input is empty
     * @param {boolean} closeSuggestionsIfInputIsEmpty Close suggestions if input is empty
     * @api
     */
    Autocomplete.prototype.setCloseSuggestionsIfInputIsEmpty = function (closeSuggestionsIfInputIsEmpty) {
        this.closeSuggestionsIfInputIsEmpty_ = closeSuggestionsIfInputIsEmpty;
    };
    /**
     * @api
     * @return {boolean} Return if suggestions list is visible
     */
    Autocomplete.prototype.getSuggestionsListVisibility = function () {
        return this.vAutocomplete_.getSuggestionsListVisibility();
    };
    /**
     * Current input value
     * @api
     * @return {string} Input value
     */
    Autocomplete.prototype.getValue = function () {
        return this.vAutocomplete_.getValue();
    };
    /**
     * Enable input form
     */
    Autocomplete.prototype.enable = function () {
        this.vAutocomplete_.enable();
    };
    /**
     * Disable input form
     */
    Autocomplete.prototype.disable = function () {
        this.vAutocomplete_.disable();
    };
    return Autocomplete;
}(Service));
export default Autocomplete;
//# sourceMappingURL=Autocomplete.js.map